import { OnboardingInfoFormDataWithValidation } from '~/pages/Onboarding/info/OnboardingInfo';
import { BusinessNeed, BusinessType } from '~/pages/Onboarding/onboarding.constants';
import {
  type OnboardingInitialData,
  type OnboardingOrdersValuesWithAddressObject,
} from '~/pages/Onboarding/onboarding.types';
import { type OnboardingStepCompleted } from '~/pages/Onboarding2024/types';
import { OnboardingOrdersValues } from '~/pages/Onboarding/orders/OnboardingOrders';
import Address from '~/types/Address';

export enum OnboardingActionKeys {
  START_ONBOARDING = 'START_ONBOARDING',
  INITIALIZE_ONBOARDING = 'INITIALIZE_ONBOARDING',
  PREPOPULATE_ONBOARDING = 'PREPOPULATE_ONBOARDING',
  NAVIGATE = 'NAVIGATE',
  UPDATE_ONBOARDING_STRUCTURE = 'UPDATE_ONBOARDING_STRUCTURE',
  COMPLETE_STEP = 'COMPLETE_STEP',
  SEND_ONBOARDING_TRACK_EVENT = 'SEND_ONBOARDING_TRACK_EVENT',
  UPDATE_ONBOARDING_INFO = 'UPDATE_ONBOARDING_INFO',
  UPDATE_ONBOARDING_INFO_SUCCESS = 'UPDATE_ONBOARDING_INFO_SUCCESS',
  UPDATE_ONBOARDING_INFO_ERROR = 'UPDATE_ONBOARDING_INFO_ERROR',
  UPDATE_ONBOARDING_PREFERENCES = 'UPDATE_ONBOARDING_PREFERENCES',
  UPDATE_ONBOARDING_PREFERENCES_SUCCESS = 'UPDATE_ONBOARDING_PREFERENCES_SUCCESS',
  UPDATE_ONBOARDING_PREFERENCES_ERROR = 'UPDATE_ONBOARDING_PREFERENCES_ERROR',
  UPDATE_ONBOARDING_ORDERS = 'UPDATE_ONBOARDING_ORDERS',
  UPDATE_ONBOARDING_ORDERS_SUCCESS = 'UPDATE_ONBOARDING_ORDERS_SUCCESS',
  UPDATE_ONBOARDING_ORDERS_ERROR = 'UPDATE_ONBOARDING_ORDERS_ERROR',
  UPDATE_ONBOARDING_PRO_PLAN = 'UPDATE_ONBOARDING_PRO_PLAN',
  UPDATE_ONBOARDING_PRO_PLAN_ERROR = 'UPDATE_ONBOARDING_PRO_PLAN_ERROR',
  COMPLETE_ONBOARDING = 'COMPLETE_ONBOARDING',
  UPDATE_ONBOARDING_ADDRESSES = 'UPDATE_ONBOARDING_ADDRESSES',
  TRACK_STEP_COMPLETED = 'TRACK_STEP_COMPLETED',
  UPDATE_HAS_SUBMITTED_HUBSPOT_FORM = 'UPDATE_HAS_SUBMITTED_HUBSPOT_FORM',
  CLEAR_GLOBAL_ERRORS = 'CLEAR_GLOBAL_ERRORS',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function onboardingTrackEvent(name: string, properties?: { [key: string]: any }) {
  return <const>{
    type: OnboardingActionKeys.SEND_ONBOARDING_TRACK_EVENT,
    payload: {
      name: name,
      properties: properties,
    },
  };
}

export function startOnboarding() {
  return <const>{
    type: OnboardingActionKeys.START_ONBOARDING,
  };
}

export function initializeOnboarding(route: string | null) {
  return <const>{
    type: OnboardingActionKeys.INITIALIZE_ONBOARDING,
    payload: {
      route,
    },
  };
}

export function prepopulateOnboarding(values: OnboardingInitialData) {
  return <const>{
    type: OnboardingActionKeys.PREPOPULATE_ONBOARDING,
    payload: {
      values,
    },
  };
}

export function navigate(index: number) {
  return <const>{
    type: OnboardingActionKeys.NAVIGATE,
    payload: {
      index,
    },
  };
}

export function completeStep(index: number) {
  return <const>{
    type: OnboardingActionKeys.COMPLETE_STEP,
    payload: {
      index,
    },
  };
}

export function updateOnboardingStructure(
  businessType: BusinessType,
  businessNeed: keyof typeof BusinessNeed,
  storeName?: string,
) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_STRUCTURE,
    payload: {
      businessType,
      businessNeed,
      storeName,
    },
  };
}

// token is needed for the case when the user is updating their info
// right after signup. In this case, the user is not authenticated just yet
export function updateOnboardingInfo(values: OnboardingInfoFormDataWithValidation, token?: string) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_INFO,
    payload: {
      values,
      token,
    },
  };
}

export function updateOnboardingInfoSuccess(values: OnboardingInfoFormDataWithValidation) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_INFO_SUCCESS,
    payload: {
      values,
    },
  };
}

export function updateOnboardingInfoError(error: Error | string) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_INFO_ERROR,
    payload: {
      error,
    },
  };
}

export function updateOnboardingPreferences(
  primaryCarrier: string | number | undefined,
  labelSize: string | number | undefined,
  address?: Address,
  applyInsuranceOnAll?: boolean,
  complete?: boolean,
) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES,
    payload: {
      primaryCarrier: primaryCarrier,
      labelSize: labelSize,
      address,
      applyInsuranceOnAll,
      complete,
    },
  };
}

export function updateOnboardingPreferencesSuccess(
  primaryCarrier: string | number | undefined,
  labelSize: string | number | undefined,
  address?: Address,
) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES_SUCCESS,
    payload: {
      primaryCarrier: primaryCarrier,
      labelSize: labelSize,
      address,
    },
  };
}

export function updateOnboardingPreferencesError(error: Error | string) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES_ERROR,
    payload: {
      error,
    },
  };
}

export function updateOnboardingOrders(values: OnboardingOrdersValues) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS,
    payload: {
      ...values,
    },
  };
}

export function updateOnboardingOrdersSuccess(values: OnboardingOrdersValuesWithAddressObject) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS_SUCCESS,
    payload: {
      ...values,
    },
  };
}

export function updateOnboardingOrdersError(error: Error | string) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS_ERROR,
    payload: {
      error,
    },
  };
}

export function updateOnboardingAddresses(address: Address) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_ADDRESSES,
    payload: {
      address,
    },
  };
}

export function completeOnboarding(url?: string) {
  return <const>{
    type: OnboardingActionKeys.COMPLETE_ONBOARDING,
    payload: {
      url,
    },
  };
}

export function trackStepCompleted(
  view: string,
  action: string,
  override: OnboardingStepCompleted = {},
) {
  return <const>{
    type: OnboardingActionKeys.TRACK_STEP_COMPLETED,
    payload: {
      view,
      action,
      override,
    },
  };
}

export function updateHasSubmittedHubspotForm() {
  return <const>{
    type: OnboardingActionKeys.UPDATE_HAS_SUBMITTED_HUBSPOT_FORM,
  };
}

export function clearGlobalErrors() {
  return <const>{
    type: OnboardingActionKeys.CLEAR_GLOBAL_ERRORS,
  };
}

export function updateOnboardingPlan(plan: string, completeOnboarding: boolean) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_PRO_PLAN,
    payload: {
      plan,
      completeOnboarding,
    },
  };
}

export function updateOnboardingProPlanError(error: Error | string) {
  return <const>{
    type: OnboardingActionKeys.UPDATE_ONBOARDING_PRO_PLAN_ERROR,
    payload: {
      error,
    },
  };
}

export type OnboardingActionTypes =
  | ReturnType<typeof startOnboarding>
  | ReturnType<typeof initializeOnboarding>
  | ReturnType<typeof prepopulateOnboarding>
  | ReturnType<typeof navigate>
  | ReturnType<typeof updateOnboardingStructure>
  | ReturnType<typeof completeStep>
  | ReturnType<typeof onboardingTrackEvent>
  | ReturnType<typeof updateOnboardingInfo>
  | ReturnType<typeof updateOnboardingInfoSuccess>
  | ReturnType<typeof updateOnboardingInfoError>
  | ReturnType<typeof updateOnboardingPreferences>
  | ReturnType<typeof updateOnboardingPreferencesSuccess>
  | ReturnType<typeof updateOnboardingPreferencesError>
  | ReturnType<typeof updateOnboardingOrders>
  | ReturnType<typeof updateOnboardingOrdersSuccess>
  | ReturnType<typeof updateOnboardingOrdersError>
  | ReturnType<typeof updateOnboardingAddresses>
  | ReturnType<typeof trackStepCompleted>
  | ReturnType<typeof completeOnboarding>
  | ReturnType<typeof updateHasSubmittedHubspotForm>
  | ReturnType<typeof clearGlobalErrors>
  | ReturnType<typeof updateOnboardingProPlanError>
  | ReturnType<typeof updateOnboardingPlan>;
